
/**
 * CDN Entry Point
 * 
 * This file is the entry point for the CDN bundle.
 * It exposes the SDK on window.segmentflow.
 */

import { SegmentflowWeb } from "./web.js";
import { createUtmPlugin } from "./plugins/utm.plugin.js";
import { createShopifyPlugin } from "./plugins/shopify.plugin.js";
import { createWooCommercePlugin } from "./plugins/woocommerce.plugin.js";
import { createFormsPlugin } from "./plugins/forms.plugin.js";
import { renderForm as renderFormInternal, type PublicFormData } from "./plugins/forms/form-renderer.js";
import { createDisplayContainer } from "./plugins/forms/display-container.js";

export interface SegmentflowConfig {
  writeKey: string;
  host?: string;
  debug?: boolean;
  consentRequired?: boolean;
  autoPageTrack?: boolean;
}

// Singleton instance
let instance: SegmentflowWeb | null = null;

/**
 * Initialize the Segmentflow SDK
 */
function init(config: SegmentflowConfig): SegmentflowWeb {
  if (instance) {
    console.warn("[Segmentflow] SDK already initialized");
    return instance;
  }

  instance = new SegmentflowWeb({
    writeKey: config.writeKey,
    host: config.host ?? "https://api.segmentflow.ai",
  });

  // Auto-register plugins
  instance.register(createUtmPlugin());
  instance.register(createShopifyPlugin());
  instance.register(createWooCommercePlugin());
  instance.register(createFormsPlugin());

  if (config.debug) {
    console.log("[Segmentflow] SDK initialized", {
      writeKey: config.writeKey.substring(0, 8) + "...",
      host: config.host,
    });
  }

  return instance;
}

/**
 * Get the SDK instance (throws if not initialized)
 */
function getInstance(): SegmentflowWeb {
  if (!instance) {
    throw new Error("[Segmentflow] SDK not initialized. Call segmentflow.init() first.");
  }
  return instance;
}

/**
 * Render a form into a target element.
 *
 * Usage:
 *   segmentflow.renderForm("websiteFormId", document.getElementById("my-form"))
 *
 * Fetches the form config from the API and renders it inline using Shadow DOM.
 */
async function renderForm(websiteFormId: string, target: HTMLElement): Promise<void> {
  const sdk = getInstance();
  const host = sdk.host;

  const res = await fetch(`${host}/api/v1/public/forms/${encodeURIComponent(websiteFormId)}`);
  if (!res.ok) {
    throw new Error(`[Segmentflow] Failed to fetch form ${websiteFormId}: ${res.status}`);
  }
  const formData = (await res.json()) as PublicFormData;

  const container = createDisplayContainer({
    displayType: "Inline",
    targetElement: target,
  });
  await renderFormInternal(container.host, formData, host, sdk);
  target.setAttribute("data-sf-rendered", "true");
}

// Proxy methods to instance
const segmentflow = {
  init,
  renderForm,
  identify: (...args: Parameters<SegmentflowWeb["identify"]>) => getInstance().identify(...args),
  track: (...args: Parameters<SegmentflowWeb["track"]>) => getInstance().track(...args),
  page: (...args: Parameters<SegmentflowWeb["page"]>) => getInstance().page(...args),
  reset: () => getInstance().reset(),
  flush: () => getInstance().flush(),
  register: (plugin: Parameters<SegmentflowWeb["register"]>[0]) => getInstance().register(plugin),
};

// Expose on window
if (typeof window !== "undefined") {
  (window as any).segmentflow = segmentflow;
}

export { segmentflow, SegmentflowWeb };
